import { ContextMenu as BaseContextMenu } from '@base-ui/react/context-menu';
import { Menu as BaseMenu } from '@base-ui/react/menu';
import { RiCheckLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { cx } from './lib/tv';
import { renderIcon, type IconLike } from './icon';
import { usePortalContainer, type PortalContainer } from './lib/portal';
import { dropdownItem, dropdownSurface, popupMotion } from './lib/surfaces';

/* ================================================================================================
 * Menu / DropdownMenu — the drawn row-actions menu "File Options" 14:20659 ("Tahrirlash / O'chirish"):
 * surface padding 4, gap 4, r4; rows h32, padding 0/8, gap 12, icon 20 White/60, label 400 14/20 White/90;
 * destructive rows in red (#ff0b0f ≈ Red/1000); separators 1px White/10.
 * ============================================================================================== */

export interface MenuItemProps {
  label: React.ReactNode;
  icon?: IconLike;
  /** Called when the item is chosen (click, Enter, Space). */
  onSelect?: () => void;
  /** Red label + icon ("O'chirish"). */
  danger?: boolean;
  disabled?: boolean;
  /** Right-aligned hint, e.g. a shortcut "Ctrl+S". */
  shortcut?: React.ReactNode;
  /** Keep the menu open after selecting. */
  keepOpen?: boolean;
  /** Check mark on the right (selected filter, sort…). */
  checked?: boolean;
  className?: string;
}

export type MenuItemSpec =
  | ({ type?: 'item'; key?: string } & Omit<MenuItemProps, 'className'>)
  | { type: 'separator'; key?: string }
  | { type: 'label'; key?: string; label: React.ReactNode };

const itemCls = (danger?: boolean) => cn(dropdownItem, danger && 'text-red-1000');
const iconCls = (danger?: boolean) => cn('size-20 shrink-0', danger ? 'text-red-1000' : 'text-icon');

export function MenuItem({
  label,
  icon,
  onSelect,
  danger,
  disabled,
  shortcut,
  keepOpen,
  checked,
  className,
}: MenuItemProps) {
  return (
    <BaseMenu.Item
      disabled={disabled}
      closeOnClick={!keepOpen}
      onClick={() => onSelect?.()}
      className={cn(itemCls(danger), className)}
    >
      {renderIcon(icon, iconCls(danger))}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {shortcut && <span className="shrink-0 text-xs text-fg-secondary">{shortcut}</span>}
      {checked && <RiCheckLine aria-hidden className="size-16 shrink-0 text-accent-2" />}
    </BaseMenu.Item>
  );
}

export function MenuSeparator() {
  return <BaseMenu.Separator className="mx-0 my-0 h-px shrink-0 bg-line" />;
}

export function MenuLabel({ children }: { children: React.ReactNode }) {
  return <div className="px-8 pt-4 pb-2 t-caps">{children}</div>;
}

function renderItems(items: readonly MenuItemSpec[]) {
  return items.map((item, i) => {
    const key = item.key ?? i;
    if (item.type === 'separator') return <MenuSeparator key={key} />;
    if (item.type === 'label') return <MenuLabel key={key}>{item.label}</MenuLabel>;
    const { type: _type, key: _key, ...rest } = item;
    return <MenuItem key={key} {...rest} />;
  });
}

export interface MenuProps {
  /** The trigger element (IconButton `more-line`, Button with a chevron…). */
  trigger: React.ReactElement;
  /** Items as data, or compose `MenuItem` / `MenuSeparator` children. */
  items?: readonly MenuItemSpec[];
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  /** Distance from the trigger in CSS px. Default 4. */
  sideOffset?: number;
  /** Minimum width in design px. Default 155 (14:20659). */
  minWidth?: number;
  /** Open on hover as well. */
  openOnHover?: boolean;
  disabled?: boolean;
  className?: string;
  container?: PortalContainer;
}

export function Menu({
  trigger,
  items,
  children,
  open,
  onOpenChange,
  side = 'bottom',
  align = 'end',
  sideOffset = 4,
  minWidth = 155,
  openOnHover,
  disabled,
  className,
  container,
}: MenuProps) {
  const portalContainer = usePortalContainer(container);
  return (
    <BaseMenu.Root open={open} onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined} disabled={disabled}>
      <BaseMenu.Trigger render={trigger} openOnHover={openOnHover} />
      <BaseMenu.Portal container={portalContainer}>
        <BaseMenu.Positioner side={side} align={align} sideOffset={sideOffset} collisionPadding={8} className="z-75 outline-none">
          <BaseMenu.Popup
            style={{ minWidth: `${minWidth / 16}rem` }}
            className={cx(dropdownSurface, popupMotion, 'flex max-h-(--available-height) flex-col gap-4 overflow-y-auto', className)}
          >
            {items ? renderItems(items) : children}
          </BaseMenu.Popup>
        </BaseMenu.Positioner>
      </BaseMenu.Portal>
    </BaseMenu.Root>
  );
}

export interface ContextMenuProps {
  /** The area that opens the menu on right-click / long-press. */
  children: React.ReactElement;
  items: readonly MenuItemSpec[];
  minWidth?: number;
  disabled?: boolean;
  className?: string;
  container?: PortalContainer;
}

/** Right-click menu with the same surface (Base UI ContextMenu). */
export function ContextMenu({ children, items, minWidth = 155, disabled, className, container }: ContextMenuProps) {
  const portalContainer = usePortalContainer(container);
  return (
    <BaseContextMenu.Root disabled={disabled}>
      <BaseContextMenu.Trigger render={children} />
      <BaseContextMenu.Portal container={portalContainer}>
        <BaseContextMenu.Positioner className="z-75 outline-none">
          <BaseContextMenu.Popup
            style={{ minWidth: `${minWidth / 16}rem` }}
            className={cx(dropdownSurface, popupMotion, 'flex flex-col gap-4', className)}
          >
            {items.map((item, i) => {
              const key = item.key ?? i;
              if (item.type === 'separator') return <BaseContextMenu.Separator key={key} className="h-px shrink-0 bg-line" />;
              if (item.type === 'label') return <MenuLabel key={key}>{item.label}</MenuLabel>;
              return (
                <BaseContextMenu.Item
                  key={key}
                  disabled={item.disabled}
                  closeOnClick={!item.keepOpen}
                  onClick={() => item.onSelect?.()}
                  className={itemCls(item.danger)}
                >
                  {renderIcon(item.icon, iconCls(item.danger))}
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.shortcut && <span className="shrink-0 text-xs text-fg-secondary">{item.shortcut}</span>}
                </BaseContextMenu.Item>
              );
            })}
          </BaseContextMenu.Popup>
        </BaseContextMenu.Positioner>
      </BaseContextMenu.Portal>
    </BaseContextMenu.Root>
  );
}
